import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { chatApi } from '@/lib/api';

interface EmbedCodeFieldProps {
  workflowId?: string;
}

/**
 * EmbedCodeField - Displays and allows copying of embed code
 * Fetches the embed code from the backend dynamically
 */
export function EmbedCodeField({ workflowId: propWorkflowId }: EmbedCodeFieldProps) {
  // Get workflow ID from URL params as fallback
  const { id } = useParams<{ id: string }>();
  const workflowId = propWorkflowId || id;
  
  const [embedCode, setEmbedCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchEmbedCode = async () => {
      console.log('[EmbedCodeField] Workflow ID:', workflowId);
      
      // Check if workflowId exists
      if (!workflowId) {
        setEmbedCode('💡 Save your workflow first to generate embed code\n\nThe embed code will appear here after you save the workflow.');
        setLoading(false);
        return;
      }

      try {
        console.log('[EmbedCodeField] Fetching embed code for:', workflowId);
        const response = await chatApi.getEmbedCode(workflowId);
        console.log('[EmbedCodeField] Response:', response.data);
        setEmbedCode(response.data.embedCode || '');
        setLoading(false);
      } catch (err: any) {
        console.error('[EmbedCodeField] Failed to fetch embed code:', err);
        console.error('[EmbedCodeField] Error response:', err.response?.data);
        setError('Failed to load embed code. Make sure the workflow is saved and activated.');
        setLoading(false);
      }
    };

    fetchEmbedCode();
  }, [workflowId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Embed Code</label>
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Embed Code</label>
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Embed Code</label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 text-xs"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 mr-1" />
                Copy
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            asChild
            className="h-7 text-xs"
          >
            <a
              href={`/embed/${workflowId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Preview
            </a>
          </Button>
        </div>
      </div>
      <div className="bg-muted/50 border rounded-md p-3">
        <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
          {embedCode}
        </pre>
      </div>
      <p className="text-xs text-muted-foreground">
        Copy this code and paste it into your website to embed the chat widget
      </p>
    </div>
  );
}
